import axios, { AxiosInstance } from "axios";
import { ServiceType, MessageSenderType, Message, Attachment } from "../contexts/SettingsContext";
import { getStandardPreprompt } from "./Preprompt";
import { csvToRows } from "./RangeUtils";

const formatCellValue = (value: string): string => {
  const trimmed = (value ?? "").trim();
  if (trimmed === "") return '""';
  if (!Number.isNaN(Number(trimmed))) return trimmed;
  return `"${trimmed.replace(/"/g, '""')}"`;
};

interface ChatCompletionRequest {
  model: string;
  messages: Array<{
    role: MessageSenderType;
    content: string;
  }>;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
}

interface ChatCompletionResponse {
  id: string;
  object: string;
  created: number;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

interface AnthropicRequest {
  model: string;
  max_tokens: number;
  system?: string;
  messages: Array<{
    role: "user" | "assistant";
    content: string;
  }>;
  temperature?: number;
  stream?: boolean;
}

interface AnthropicResponse {
  id: string;
  type: string;
  role: string;
  content: Array<{
    type: string;
    text?: string;
  }>;
  model: string;
  stop_reason: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

interface AgentCoreRequest {
  prompt: string;
  media?: {
    type: string;
    format: string;
    data: string;
  };
}

interface AgentCoreResponse {
  contentType?: string;
  response?: string;
  text?: string;
}

export class APIService {
  private client: AxiosInstance;
  private serviceType: ServiceType;
  private apiKey: string;
  private apiUrl: string;

  constructor(serviceType: ServiceType, apiKey: string, apiUrl: string = "") {
    this.serviceType = serviceType;
    this.apiKey = apiKey;
    this.apiUrl = apiUrl;
    this.client = axios.create();
    this.setupClient();
  }

  private setupClient() {
    // Set up headers based on service type
    switch (this.serviceType) {
      case "openai":
        this.client.defaults.headers.common["Authorization"] = `Bearer ${this.apiKey}`;
        this.apiUrl = this.apiUrl || "https://api.openai.com/v1";
        break;

      case "azure":
        this.client.defaults.headers.common["api-key"] = this.apiKey;
        // Azure OpenAI requires the full endpoint URL
        break;

      case "openai-compatible":
      case "bedrock":
      case "bedrock-agentcore":
        this.client.defaults.headers.common["Authorization"] = `Bearer ${this.apiKey}`;
        // Use the provided custom URL
        break;

      case "anthropic":
        this.client.defaults.headers.common["x-api-key"] = this.apiKey;
        this.client.defaults.headers.common["anthropic-version"] = "2023-06-01";
        this.apiUrl = this.apiUrl || "https://api.anthropic.com/v1";
        break;

      default:
        throw new Error(`Unsupported service type: ${this.serviceType}`);
    }

    this.client.defaults.headers.common["Content-Type"] = "application/json";
    this.client.defaults.baseURL = this.apiUrl;
  }

  /**
   * Prepare messages array including preprompt and full history
   */
  private prepareMessages(
    userMessage: string,
    attachments: (string | Attachment)[] = [],
    history: Message[] = []
  ): Array<{ role: "system" | "user" | "assistant"; content: string }> {
    const messages: Array<{ role: "system" | "user" | "assistant"; content: string }> = [];

    // Add system message with preprompt
    messages.push({
      role: "system",
      content: getStandardPreprompt(),
    });

    // Add conversation history (if any)
    history.forEach((msg) => {
      messages.push({
        role: msg.role,
        content: this.formatMessageContent(msg.content, msg.attachments),
      });
    });

    // Add current user message with attachments
    messages.push({
      role: "user",
      content: this.formatMessageContent(userMessage, attachments),
    });

    return messages;
  }

  /**
   * Format message content including attachments
   */
  private formatMessageContent(content: string, attachments?: (string | Attachment)[]): string {
    if (!attachments || attachments.length === 0) {
      return content;
    }

    const formatted = attachments
      .map((attachment) => {
        if (typeof attachment === "string") {
          return attachment;
        }

        const dataStartsAt = attachment.firstDataRow;
        const headerRow = attachment.hasHeaderRow ? dataStartsAt - 1 : null;

        // Map each column to its Excel column letter so the model does not need to count.
        const columns = (attachment.columnLetters || []).map((letter, index) => {
          const name =
            headerRow !== null ? attachment.headers[index] : attachment.headers[index] || letter;
          return name ? `${letter}: ${name}` : letter;
        });

        // Build explicit per-cell address -> value references so the model uses the
        // real source cells (e.g. G7) instead of assuming columns A, B, C.
        const bodyRows = csvToRows(attachment.dataCsv || "");
        const letters = attachment.columnLetters || [];
        const cellRefs: string[] = [];

        if (headerRow !== null) {
          (attachment.headers || []).forEach((name, index) => {
            const letter = letters[index];
            if (letter) {
              const value = name !== undefined && name !== "" ? name : letter;
              cellRefs.push(`${letter}${headerRow} = ${formatCellValue(value)}`);
            }
          });
        }

        bodyRows.forEach((row, rowOffset) => {
          const rowNum = dataStartsAt + rowOffset;
          row.forEach((cell, index) => {
            const letter = letters[index];
            if (letter) {
              cellRefs.push(`${letter}${rowNum} = ${formatCellValue(cell)}`);
            }
          });
        });

        const cellMap =
          cellRefs.length > 0 ? `Cell references (address = value):\n${cellRefs.join("\n")}` : null;

        const details = [
          `Source range: ${attachment.range}`,
          columns.length > 0 ? `Columns:\n${columns.join("\n")}` : null,
          headerRow !== null
            ? `Rows: row ${headerRow} contains the column headers; data starts at row ${dataStartsAt}.`
            : `Data starts at row ${dataStartsAt}.`,
        ]
          .filter(Boolean)
          .join("\n\n");

        const instruction = [
          "IMPORTANT: When writing formulas, always use the exact cell addresses above (e.g. G7, H7, I7).",
          "Do not assume the data is in columns A, B, or C or in a different row.",
        ].join(" ");

        return [details, cellMap, instruction, `Data:\n${attachment.dataCsv || ""}`]
          .filter(Boolean)
          .join("\n\n");
      })
      .join("\n\n");

    return `${content}\n\n[Excel Data Attachment]:\n${formatted}`;
  }

  /**
   * Send chat completion request
   */
  async sendChatCompletion(
    userMessage: string,
    attachments: (string | Attachment)[] = [],
    history: Message[] = [],
    options: {
      model?: string;
      temperature?: number;
      maxTokens?: number;
    } = {}
  ): Promise<string> {
    const messages = this.prepareMessages(userMessage, attachments, history);

    if (this.serviceType === "anthropic") {
      return this.sendAnthropicRequest(messages, options);
    }

    if (this.serviceType === "bedrock-agentcore") {
      return this.sendAgentCoreRequest(messages);
    }

    const requestBody: ChatCompletionRequest = {
      model: options.model || this.getDefaultModel(),
      messages,
      temperature: options.temperature ?? 0.7,
      max_tokens: options.maxTokens ?? 2000,
      stream: false,
    };

    let endpoint = "/chat/completions";

    // Azure OpenAI has a different endpoint structure
    if (this.serviceType === "azure") {
      // Extract deployment name from URL or use default
      const deploymentName = this.extractDeploymentName() || "gpt-35-turbo";
      endpoint = `/openai/deployments/${deploymentName}/chat/completions?api-version=2023-12-01-preview`;
    }

    try {
      const response = await this.client.post<ChatCompletionResponse>(endpoint, requestBody);

      if (response.data.choices && response.data.choices.length > 0) {
        return response.data.choices[0].message.content.trim();
      } else {
        throw new Error("No response from API");
      }
    } catch (error) {
      console.error("API request failed:", error);

      // Provide more specific error messages
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error("Invalid API key. Please check your settings.");
        } else if (error.response?.status === 404) {
          throw new Error("API endpoint not found. Please check your API URL.");
        } else if (error.response?.data?.error?.message) {
          throw new Error(`API error: ${error.response.data.error.message}`);
        } else {
          throw new Error(`API request failed: ${error.message}`);
        }
      }

      throw new Error("Failed to get response from AI service");
    }
  }

  /**
   * Send a request to the Anthropic Messages API.
   * The Anthropic API uses a different request/response format than OpenAI.
   */
  private async sendAnthropicRequest(
    messages: Array<{ role: "system" | "user" | "assistant"; content: string }>,
    options: { model?: string; temperature?: number; maxTokens?: number } = {}
  ): Promise<string> {
    const system = messages.find((m) => m.role === "system")?.content;

    const requestBody: AnthropicRequest = {
      model: options.model || this.getDefaultModel(),
      max_tokens: options.maxTokens ?? 2000,
      messages: messages
        .filter((m) => m.role !== "system")
        .map((m) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
        })),
      temperature: options.temperature ?? 0.7,
      stream: false,
    };

    if (system) {
      requestBody.system = system;
    }

    try {
      const response = await this.client.post<AnthropicResponse>("/messages", requestBody);

      const textBlocks = (response.data.content || []).filter((b) => b.type === "text");
      const text = textBlocks
        .map((b) => b.text || "")
        .join("")
        .trim();
      if (text) {
        return text;
      }
      throw new Error("No response from API");
    } catch (error) {
      console.error("API request failed:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error("Invalid API key. Please check your settings.");
        } else if (error.response?.status === 404) {
          throw new Error("API endpoint not found. Please check your API URL.");
        } else if (error.response?.data?.error?.message) {
          throw new Error(`API error: ${error.response.data.error.message}`);
        } else {
          throw new Error(`API request failed: ${error.message}`);
        }
      }

      throw new Error("Failed to get response from AI service");
    }
  }

  /**
   * Send a request to an Amazon Bedrock AgentCore runtime agent.
   * The AgentCore InvokeAgentRuntime API takes a prompt and returns agent output.
   */
  private async sendAgentCoreRequest(
    messages: Array<{ role: "system" | "user" | "assistant"; content: string }>
  ): Promise<string> {
    const prompt = messages.map((m) => `${m.role}: ${m.content}`).join("\n\n");

    const requestBody: AgentCoreRequest = {
      prompt,
    };

    try {
      const response = await this.client.post<AgentCoreResponse>("/", requestBody);

      const data = response.data;
      if (data.text) {
        return data.text.trim();
      }

      const responseContent = data.response;
      if (typeof responseContent === "string") {
        return responseContent.trim();
      }

      throw new Error("No response from API");
    } catch (error) {
      console.error("AgentCore request failed:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error("Invalid API key. Please check your settings.");
        } else if (error.response?.status === 404) {
          throw new Error("Agent endpoint not found. Please check your API URL.");
        } else if (error.response?.data?.message) {
          throw new Error(`API error: ${error.response.data.message}`);
        } else if (error.response?.data?.error?.message) {
          throw new Error(`API error: ${error.response.data.error.message}`);
        } else {
          throw new Error(`API request failed: ${error.message}`);
        }
      }

      throw new Error("Failed to get response from AI service");
    }
  }

  /**
   * Stream chat completion (for real-time responses)
   */
  async *streamChatCompletion(
    userMessage: string,
    attachments: (string | Attachment)[] = [],
    history: Message[] = [],
    options: {
      model?: string;
      temperature?: number;
      maxTokens?: number;
    } = {}
  ): AsyncGenerator<string, void, unknown> {
    const messages = this.prepareMessages(userMessage, attachments, history);

    const requestBody: ChatCompletionRequest = {
      model: options.model || this.getDefaultModel(),
      messages,
      temperature: options.temperature ?? 0.7,
      max_tokens: options.maxTokens ?? 2000,
      stream: true,
    };

    let endpoint = "/chat/completions";

    if (this.serviceType === "azure") {
      const deploymentName = this.extractDeploymentName() || "gpt-35-turbo";
      endpoint = `/openai/deployments/${deploymentName}/chat/completions?api-version=2023-12-01-preview`;
    }

    try {
      const response = await fetch(`${this.apiUrl}${endpoint}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody),
        mode: "no-cors",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Response body is not readable");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ") && line !== "data: [DONE]") {
              try {
                const data = JSON.parse(line.substring(6));
                if (data.choices[0]?.delta?.content) {
                  yield data.choices[0].delta.content;
                }
              } catch {
                // Ignore parse errors for incomplete chunks
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error("Stream request failed:", error);
      throw error;
    }
  }

  /**
   * Get headers for fetch requests
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    switch (this.serviceType) {
      case "openai":
        headers["Authorization"] = `Bearer ${this.apiKey}`;
        break;
      case "azure":
        headers["api-key"] = this.apiKey;
        break;
      case "openai-compatible":
      case "bedrock":
      case "bedrock-agentcore":
        headers["Authorization"] = `Bearer ${this.apiKey}`;
        break;
      case "anthropic":
        headers["x-api-key"] = this.apiKey;
        headers["anthropic-version"] = "2023-06-01";
        break;
    }

    return headers;
  }

  /**
   * Get default model based on service type
   */
  private getDefaultModel(): string {
    switch (this.serviceType) {
      case "openai":
        return "gpt-3.5-turbo";
      case "azure":
        return "gpt-35-turbo";
      case "openai-compatible":
        return "gpt-3.5-turbo";
      case "anthropic":
        return "claude-sonnet-4-6";
      case "bedrock":
        return "anthropic.claude-3-sonnet-20240229-v1:0";
      case "bedrock-agentcore":
        return "default";
      default:
        return "gpt-3.5-turbo";
    }
  }

  /**
   * Extract deployment name from Azure URL
   */
  private extractDeploymentName(): string | null {
    if (this.serviceType !== "azure") return null;

    const match = this.apiUrl.match(/deployments\/([^/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Validate API configuration
   */
  async validateConfig(): Promise<boolean> {
    try {
      // Send a simple test request
      await this.sendChatCompletion("Hello", [], [], {
        maxTokens: 5,
      });
      return true;
    } catch {
      return false;
    }
  }
}

// Factory function to create API service
export const createAPIService = (
  serviceType: ServiceType,
  apiKey: string,
  apiUrl: string = ""
): APIService => {
  return new APIService(serviceType, apiKey, apiUrl);
};
